import { AsyncPipe, NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StorageTemporaryLinkPipe } from '@interacta-shared/data-access-storage-temporary-link';
import {
    ApplyPipe,
    BadgeComponent,
    ImageComponent,
    Size,
} from '@interacta-shared/ui';
import { CreationDateComponent } from '@interacta-shared/ui-common';
import { getRandomSecondaryColorVariables } from '@interacta-shared/util';
import { AISharedAttachmentChipComponent } from '@modules/ai-shared/components/ai-shared-attachment-chip/ai-shared-attachment-chip.component';
import {
    getReferencePreviewText,
    getReferenceTitle,
    getReferenceUnavailableLabel,
} from '@modules/ai-shared/helpers/ai-shared.utils';
import {
    AISharedGenericReference,
    AISharedReferenceType,
} from '@modules/ai-shared/models/ai-shared.model';
import { getCustomPostPreviewImage } from '@modules/post/utils/post.utils';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'interacta-ai-shared-reference-chip',
    templateUrl: './ai-shared-reference-chip.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AISharedAttachmentChipComponent,
        ApplyPipe,
        AsyncPipe,
        BadgeComponent,
        CreationDateComponent,
        ImageComponent,
        MatTooltipModule,
        NgClass,
        StorageTemporaryLinkPipe,
        TranslatePipe,
    ],
})
export class AISharedReferenceChipComponent {
    private readonly translateService = inject(TranslateService);

    readonly reference = input.required<AISharedGenericReference>();
    /** `regular` is the card above a message, `small` the one inside the question input. */
    readonly size = input<Extract<Size, 'regular' | 'small'>>('regular');

    readonly referenceClick = output();

    readonly AISharedReferenceType = AISharedReferenceType;
    readonly getCustomPostPreviewImage = getCustomPostPreviewImage;

    readonly unavailableLabel = computed(() =>
        getReferenceUnavailableLabel(this.reference()),
    );

    readonly title = computed(() =>
        getReferenceTitle(
            this.reference(),
            (key, params) =>
                this.translateService.instant(key, params) as string,
        ),
    );

    readonly postPlaceholderColor = computed(() => {
        const reference = this.reference();
        return reference.type === AISharedReferenceType.POST
            ? getRandomSecondaryColorVariables(reference.post.id)
            : null;
    });

    readonly isComment = computed(
        () => this.reference().type === AISharedReferenceType.COMMENT,
    );
    readonly thumbnailSrc = computed(() =>
        this.isComment()
            ? 'assets/images-2.0/tabs/comments.png'
            : 'assets/shared/images/pro-tip/post.png',
    );

    readonly creatorName = computed(() => {
        const reference = this.reference();
        switch (reference.type) {
            case AISharedReferenceType.POST:
                return reference.post.creatorUser?.fullName;
            case AISharedReferenceType.COMMENT:
                return reference.comment.creatorUser?.fullName;
            default:
                return undefined;
        }
    });

    /** Second line of the regular chip: the author, or the text of a comment whose title already names it. */
    readonly regularSubtitle = computed(() =>
        this.isComment() ? this.smallSubtitle() : this.creatorName(),
    );

    /** Second line of the small chip: the post's author, or the comment's text. */
    readonly smallSubtitle = computed(() =>
        this.reference().type === AISharedReferenceType.POST
            ? this.creatorName()
            : getReferencePreviewText(
                  this.reference(),
                  (key, params) =>
                      this.translateService.instant(key, params) as string,
              ),
    );
}
